/**
@format
@flow local-strict
*/

import React, { useState } from 'react';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import Icon from '@ziynx/components/icons';
import Link from '@ziynx/components/links';
import Squircle from '@ziynx/components/squircles';
import { ImageAvatar } from '@ziynx/components/images';
import type { TextLinkType } from '@ziynx/flow-types/links';
import { Constants as ThemeConstants } from '@ziynx/components/layout';
import { Body, Heading } from '@ziynx/components/typography';
import { getDimensions } from '@ziynx/components/utilities';
import ListItemWrapper from './ListItemWrapper';

export type Props = $ReadOnly<{|
  /**
   * If the list item is selected
   * used for size small only
   */
  isSelected?: boolean,
  /**
   * If listItemSquirclSmall should be disabled
   */
  disabled?: boolean,
  /**
   * Depicts when ListItemSquircleSmall is collapsed
   */
  isCollapsed?: boolean,
  /**
   * sizes for small and medium styles
   * Default medium
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * Text for ListItemSquircle
   */
  text?: string | Array<string>,
  /**
   * Label, defaults to below text, if any
   */
  label?: {
    text?: string,
    labelPosition?: 'bottom' | 'top',
    color?: string,
  },
  /**
   * larger, vertically centered heading text
   */
  heading?: string,
  /**
   * Right action, ie close icon
   * Take in name of icon
   * Will be clickable
   */
  rightActionIcon?: {
    iconName?: string,
    onClick?: () => void,
  },
  /**
   * Text link
   */
  textLink?: TextLinkType,
  /**
   * Name for the squircleIcon
   */
  iconName?: string,
  /**
   * Color for icon, if any
   * Default Gray
   */
  color?: string,
  /**
   * Image source if is squircleImage
   */
  imageSource?: string,
  /**
   * Image alt text, if imageSource
   */
  imageAltText?: string,
  /**
   * Text if squircle is a SquircleText
   */
  squircleText?: string,
  /**
   * Squircle Background color
   * Defaults to Colors.White
   */
  squircleBackgroundColor?: string,
  /**
   * Color for text
   * Default grayVeryDark
   */
  textColor?: string,
   /**
   * Width measurements
   */
  width?: string | number,
  /**
   * MaxWidth measurements
   * Default 656px
   */
  maxWidth?: string | number,
  /**
   * Override alignment
   */
  alignment?: string,
  /**
   * Custom onMouseEnter function to handle hover state if size small
   */
  onMouseEnter?: (event?: SyntheticMouseEvent<HTMLDivElement>) => void,
  /**
   * Custom onMouseLeave function to handle non-hover state of size small
   */
  onMouseLeave?: (event?: SyntheticMouseEvent<HTMLDivElement>) => void,
  /**
   * Overall listItem onClick
   */
  onClick?: () => void,
|}>;

/**
 * Function to determine width or maxWidth
 */
const findWidth = (size: string, measures: string): string => {
  switch (size) {
  case 'extraSmall':
    return measures || '639px';

  case 'small':
    return measures || '296px';

  case 'medium':
  default:
    return measures || '656px';
  }
};

/**
 * Function to find color for icon
 * if size is medium and color is not provided,
 * icon is default to grayVeryDark
 */
const findSquircleColor = (size, selected, hovered, disabled, color): string => {
  if (size === 'small' && !selected && !hovered && !disabled) {
    return color || Colors.Gray;
  }
  if (size === 'small' && disabled) {
    return Colors.GrayLight;
  }
  return color || Colors.GrayVeryDark;
};

/**
 * Function to find the color for textSection in the component
 * depending on the size, isSelected, and label
 */
const findTextColor = (size, selected, hovered, disabled, textColor): string => {
  if (size !== 'small') {
    return textColor || Colors.GrayVeryDark;
  }
  if (!selected && !hovered && !disabled) {
    return textColor || Colors.Gray;
  }
  if (selected && !hovered && !disabled) {
    return Colors.PrimaryBlue;
  }
  if (hovered && !disabled) {
    return Colors.BrandBlueDark;
  }
  return Colors.GrayLight; // if disabled
};

const findBackgroundColor = (disabled, selected, hovering, color): string => {
  if (!disabled && (selected || hovering)) {
    return Colors.BrandBlueFaded;
  }
  return color || Colors.White;
};

const _getSquircleSize = (size) => {
  switch (size) {
  case 'large':
    return 80;
  case 'small':
    return 36;
  case 'extraSmall':
    return 26;
  case 'medium':
  default:
    return 44;
  }
};

const getMinWidth = (size) => {
  switch (size) {
  case 'medium':
    return 240;
  case 'small':
    return 68;
  case 'extraSmall':
    return 240;
  default:
    return 0;
  }
};

const getAlignment = (size) => {
  if (size === 'small') {
    return 'center';
  }
  return 'flex-start';
};

const getTextPaddingLeft = (size, heading) => {
  if (size === 'extraSmall') return '12px';
  if (heading) return '24px';
  return '16px';
};

const smallTransition = 'transition: width ease-in-out 350ms, background-color ease-in-out 100ms;';

const Wrapper = styled(ListItemWrapper)`
  justify-content: space-between;
  text-align: left;
  padding: ${({ size }) => (size === 'small' ? '10px 16px 10px 16px' : '0px')};
  ${({ size }) => size === 'small' && smallTransition};
  width: ${({ width }) => getDimensions(width)};
  min-width: ${({ size }) => getMinWidth(size)}px;
  max-width: ${({ maxWidth, size }) => findWidth(size, maxWidth)};
  background-color: ${({ color }) => color};
  border-radius: ${({ size }) => (size === 'small' ? `${ThemeConstants.borderRadiusMedium}px` : '0px')};
`;

const LeftContentWrapper = styled.div`
  display: flex;
  align-items: ${({ size, alignment }) => alignment || getAlignment(size)};
  min-width: 0;
`;

const SquircleWrapper = styled.div`
  display: flex;
  height: ${({ size }) => _getSquircleSize(size)}px;
`;

const TextWrapper = styled.span`
  overflow: hidden;
  padding-left: ${({ size, heading }) => getTextPaddingLeft(size, heading)};
  margin-right: ${({ size }) => (size === 'small' ? 18 : 0)}px;
  min-width: ${({ size }) => (size === 'medium' ? 168 : 0)}px;
`;

const StyledBody = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 400ms;
  transition-delay: 400ms;
  ${({ size }) => size === 'medium'
  && 'display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; white-space: normal;'
}
}
`;

const StyledHeading = styled(Heading)`
  padding-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box; 
  -webkit-line-clamp: 2; 
  -webkit-box-orient: vertical;
  max-height: 66px;
`;

const XSHeading = styled(Heading)`
  max-height: 50px;
`;

const StyledInlineLink = styled(Link)`
  display: flex;
  height: 44px;
  width: 44px;
  align-items: center;
  justify-content: center;
`;

const StyledTextLink = styled(Link)`
  padding-top: ${({ size }) => (size === 'extraSmall' ? '2px' : '12px')};
  padding-left: ${({ size }) => (size === 'medium' ? '16px' : '12px')};
  min-width: 32px;
  max-width: ${({ size }) => (size === 'extraSmall' ? '82px' : '194px')};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/**
 * ListItemSquircle component with SquircleIcon and texts
 */
const ListItemSquircle = (props: Props): React$Node => {
  const {
    width,
    maxWidth,
    textColor,
    iconName,
    color,
    rightActionIcon,
    textLink,
    label,
    text,
    disabled,
    isCollapsed,
    squircleText,
    squircleBackgroundColor,
    isSelected,
    imageSource,
    imageAltText,
    size,
    onMouseEnter,
    onMouseLeave,
    heading,
    alignment,
    onClick,
    ...rest
  } = props;

  const [isHovering, setIsHovering] = useState(false);

  const handleOnMouseEnter = (): void => {
    if (!disabled) setIsHovering(true);

    if (onMouseEnter) onMouseEnter();
  };

  const handleOnMouseLeave = (): void => {
    if (!disabled) setIsHovering(false);

    if (onMouseLeave) onMouseLeave();
  };

  /**
   * Alignment
   */
  const defaultAlignment = getAlignment(size);
  const labelPosition = label?.labelPosition || 'top';

  /**
   * Function to render squircle portion of the component
   * could be image, icon, or text
   */
  const renderSquircle = (): React$Node => (
    <Squircle
      size={size}
      iconName={iconName}
      invertedColor={Colors.White}
      invertedBackgroundColor={Colors.BrandBlueDark}
      inverted={
        size === 'small' && isHovering && !imageSource
      }
      active={isSelected}
      disabled={disabled}
      backgroundColor={squircleBackgroundColor}
      color={findSquircleColor(size, isSelected, isHovering, disabled, color)}
      {...(size === 'small' && { noShadow: (!isSelected && !isHovering) || disabled })} // size small only
      {...{ text: squircleText }}
    />
  );

  const renderImageAvatar = (): React$Node => (
    <ImageAvatar
      size={size}
      imageSource={imageSource}
    />
  );

  /**
   * For size medium, if text is an array
   */
  const renderTextArray = (items: Array<string>): React$Node => {
    if (!items) return null;
    return items.map((item: string, index: number): React$Node => {
      const keyIndex = `item-${index}`;
      return (
        <StyledBody
          key={keyIndex}
          medium
          size={size}
          color={Colors.GrayVeryDark}
        >
          {item}
        </StyledBody>

      );
    });
  };

  /**
   * Renders text section of list item
   */
  const renderTextSection = (): React$Node => {
    const textColorVar = findTextColor(
      size,
      isSelected,
      isHovering,
      disabled,
      textColor,
    );
    return (
      <TextWrapper
        size={size}
        heading={heading}
        isCollapsed={isCollapsed}
      >
        {heading
          && (
            <StyledHeading
              medium
              color={textColor || Colors.GrayVeryDark}
            >
              {heading}
            </StyledHeading>
          )}
        {!heading && label && labelPosition === 'top'
            && (
              <StyledBody
                color={label?.color || Colors.GrayDark}
                small={size === 'small'}
                medium={size === 'medium'}
                size={size}
              >
                {label?.text}
              </StyledBody>
            )}
        {!heading && text && size !== 'extraSmall' && (
          typeof text === 'string' ? (
            <StyledBody
              medium
              color={textColorVar}
              size={size}
            >
              {text}
            </StyledBody>
          ) : renderTextArray(text))}
        {text && size === 'extraSmall' && (
          <XSHeading
            small
            color={textColorVar}
          >
            {text}
          </XSHeading>
        )}
        {label && labelPosition === 'bottom'
          && (
            <StyledBody small size={size} color={label?.color || Colors.GrayDark}>
              {label?.text}
            </StyledBody>
          )}
      </TextWrapper>
    );
  };

  /**
   * Function to render the rightAction section
   * when there's a rightActionIcon string passed
   */
  const renderRightAction = (): React$Node => {
    const {
      iconName: rightIconName,
      onClick: rightIconOnClick,
    } = rightActionIcon || {};
    if (!rightIconName) return null;
    return (
      <StyledInlineLink onClick={rightIconOnClick}>
        <Icon name={rightIconName} veryTiny color={Colors.GrayVeryDark} />
      </StyledInlineLink>
    );
  };

  return (
    <Wrapper
      alignment={alignment || defaultAlignment}
      size={size}
      width={width}
      maxWidth={maxWidth}
      heading={heading}
      text={text}
      label={label}
      color={size === 'small' ? findBackgroundColor(disabled, isSelected, isHovering) : undefined}
      disabled={disabled}
      isCollapsed={isCollapsed}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onClick={onClick}
      {...rest}
    >
      <LeftContentWrapper size={size} alignment={alignment}>
        <SquircleWrapper isCollapsed={isCollapsed} size={size}>
          {!imageSource && renderSquircle()}
          {imageSource && renderImageAvatar()}
        </SquircleWrapper>
        {renderTextSection()}
      </LeftContentWrapper>
      {rightActionIcon && rightActionIcon?.iconName ? renderRightAction() : null}
      {textLink
      && (
        <StyledTextLink
          // Size prop is used for padding and max-width in styled-component
          size={size}
          href={textLink?.href}
          target={textLink?.target}
          large
          color={textLink?.color}
          onClick={textLink?.onClick}
          variant="text"
        >
          {textLink?.children}
        </StyledTextLink>
      )}
    </Wrapper>
  );
};

ListItemSquircle.defaultProps = {
  width: '100%',
};

export default ListItemSquircle;
